// === Web App Settings Dialog — Theme picker ===

import { useState, type JSX } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../lib/theme-context';

interface SettingsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps): JSX.Element {
  const { currentTheme, changeTheme, themeOptions, gradientCards, toggleGradientCards } = useThemeContext();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSave = (): void => {
    setSnackbarOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          Settings
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Theme Color Card */}
          <Box sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Theme Color
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a colour theme for your dashboard
            </Typography>

            {/* Colour swatches grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                gap: 2,
                mb: 3,
              }}
            >
              {themeOptions.map((opt) => (
                <Box
                  key={opt.id}
                  onClick={() => { void changeTheme(opt.id); }}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 2,
                    border: currentTheme === opt.id ? '3px solid' : '1px solid',
                    borderColor: currentTheme === opt.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                  }}
                >
                  <Box sx={{ width: '100%', height: 52, borderRadius: 1, mb: 1, backgroundColor: opt.bg, border: opt.id === 'white' ? '1px solid' : 'none', borderColor: 'divider' }} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: currentTheme === opt.id ? 'bold' : 'normal' }}
                  >
                    {opt.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Display Options Card */}
          <Box sx={{ borderRadius: 2, p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Display Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={gradientCards}
                  onChange={toggleGradientCards}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Card Gradients</Typography>
                  <Typography variant="caption" color="text.secondary">Show colour gradient tints on dashboard nav cards</Typography>
                </Box>
              }
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => { void changeTheme('default'); }}
              color="inherit"
            >
              Reset to Default
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              color="primary"
            >
              Save Changes
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Theme saved successfully
        </Alert>
      </Snackbar>
    </>
  );
}
